/**
 * The kinds that can be picked here: the built-in plugins' (known by the core, sent with the
 * graph snapshot), the graph's own and those of the installed plugins that are ready
 * (`shared/kinds.ts` holds the pure helpers). Choosing a community plugin's kind the graph
 * does not have yet defines it in the same edit: one undo, and the project keeps it without
 * the plugin.
 *
 * Environments share no kinds: what can be picked (`nodes`, `links`, `suggest`) is only the
 * current environment's (see {@link kindsOfEnvironment}); everything stays drawn as it is.
 */
import { createMemo, type Accessor } from "solid-js";
import type { Actions } from "../client/actions";
import type { GraphMirror } from "../client/graph";
import type { GraphChange } from "@protocol/GraphChange";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Kinds } from "@protocol/Kinds";
import type { Link } from "@protocol/Link";
import type { Node } from "@protocol/Node";
import {
  addLinkEdits,
  addNodeEdits,
  allKinds,
  defineFor,
  linkKindEdits,
  linkKindOf,
  linkAllowed,
  linkKinds,
  nodeKindEdits,
  nodeKindOf,
  nodeKinds,
  suggestedLink,
  type LinkKindView,
  type NodeKindView,
} from "../shared/kinds";
import type { PluginInfo } from "@protocol/PluginInfo";
import { isReady, type Plugins } from "./plugins";

/**
 * The kinds of `kinds` environment `scope` offers: those of its own plugin, of plugins requiring
 * it (directly or through others, such as a language reader of coding), and those of no
 * environment (the graph's own, a plugin of kinds for any use). No environment: all of them.
 */
export function kindsOfEnvironment(kinds: Kinds, scope: string | null, plugins: readonly Pick<PluginInfo, "id" | "requires" | "environment">[]): Kinds {
  if (scope === null) return kinds;
  const environments = plugins.filter((p) => p.environment).map((p) => p.id);
  /** Whether plugin `id` is environment `env` or leads to it through what it requires. */
  const leads = (id: string, env: string, seen: Set<string> = new Set()): boolean => {
    if (id === env) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return (plugins.find((p) => p.id === id)?.requires ?? []).some((r) => leads(r, env, seen));
  };
  const belongs = (plugin: string | null | undefined) => !plugin || leads(plugin, scope) || !environments.some((env) => leads(plugin, env));
  return { nodes: kinds.nodes.filter((k) => belongs(k.plugin)), links: kinds.links.filter((k) => belongs(k.plugin)) };
}

/** The kinds service. */
export interface KindsService {
  /** Every kind that can be used: the known ones, the graph's, then the ready plugins' neither has. */
  available: Accessor<Kinds>;
  /** The node kinds to pick from (the current environment's). */
  nodes: Accessor<NodeKindView[]>;
  /** The link kinds to pick from (the current environment's). */
  links: Accessor<LinkKindView[]>;
  /** The kind a node is. */
  ofNode(node: Pick<Node, "kind">): NodeKindView;
  /** The kind a link is. */
  ofLink(link: Pick<Link, "kind">): LinkKindView;
  /** The link kind offered first between nodes of kinds `from` and `to` (none when there is no kind). */
  suggest(from: string, to: string): string | null;
  /**
   * The kind of a blueprint wire from a node of kind `from` to one of kind `to`: the first of
   * `choices` (the link kinds the environment plays) offered here and allowed between them, else
   * the suggested one.
   */
  wire(from: string, to: string, choices: readonly string[]): string | null;
  /** Makes node `id` of the kind `kindId` (one undo). */
  setNodeKind(id: string, kindId: string): Promise<GraphChange[] | null>;
  /** Makes `link` of the kind `kindId` (one undo). */
  setLinkKind(link: Link, kindId: string): Promise<GraphChange[] | null>;
  /** Adds a node, defining its kind first when it is a plugin's (one undo). */
  addNode(node: Node): Promise<GraphChange[] | null>;
  /** The `define_kinds` edit the plugin kinds `nodes` and `links` need before the graph can use them (none when it has them). */
  toDefine(nodes: string[], links: string[]): GraphEdit[];
  /** Adds a link, defining its kind first when it is a plugin's (one undo). */
  addLink(link: Link): Promise<GraphChange[] | null>;
}

/** What the kinds service is built from. */
export interface KindsDeps {
  /** The graph, for the known kinds, its own and the environment worked in. */
  graph: Pick<GraphMirror, "state" | "scope">;
  /** The plugins, for theirs. */
  plugins: Pick<Plugins, "list">;
  /** Where edits go. */
  actions: Pick<Actions, "batch">;
}

/** Creates the kinds service. */
export function createKinds({ graph, plugins, actions }: KindsDeps): KindsService {
  const fromPlugins = createMemo<Kinds>(() => {
    const ready = plugins.list().filter(isReady);
    return { nodes: ready.flatMap((p) => p.kinds?.nodes ?? []), links: ready.flatMap((p) => p.kinds?.links ?? []) };
  });
  const known = () => graph.state.known;
  const available = createMemo(() => allKinds(known(), graph.state.graph.kinds, fromPlugins()));
  /** What the current environment offers. */
  const offered = createMemo(() => kindsOfEnvironment(available(), graph.scope()?.id ?? null, plugins.list()));
  const nodes = createMemo(() => nodeKinds(offered(), known()));
  const links = createMemo(() => linkKinds(offered(), known()));
  return {
    available,
    nodes,
    links,
    ofNode: (node) => nodeKindOf(available(), node, known()),
    ofLink: (link) => linkKindOf(available(), link, known()),
    suggest: (from, to) => suggestedLink(offered(), from, to),
    wire: (from, to, choices) => choices.find((c) => offered().links.some((l) => l.id === c) && linkAllowed(offered(), c, from, to)) ?? suggestedLink(offered(), from, to),
    setNodeKind: (id, kindId) => actions.batch(nodeKindEdits(graph.state.graph, fromPlugins(), known(), id, kindId)),
    setLinkKind: (link, kindId) => actions.batch(linkKindEdits(graph.state.graph, fromPlugins(), known(), link, kindId)),
    toDefine: (nodes, ids) => defineFor(graph.state.graph, fromPlugins(), known(), nodes, ids),
    addNode: (node) => actions.batch(addNodeEdits(graph.state.graph, fromPlugins(), known(), node)),
    addLink: (link) => actions.batch(addLinkEdits(graph.state.graph, fromPlugins(), known(), link)),
  };
}
